import { useEffect } from 'react';

// ------------------------------------------------------------
// Page SEO from React: title, description, canonical, robots,
// Open Graph / Twitter tags and JSON-LD structured data.
// (Google reads these after rendering the page. For WhatsApp /
// Facebook / Instagram previews the backend also writes the same tags
// into the HTML — see routes/seoRoutes.js.)
// ------------------------------------------------------------
export const SITE_NAME = 'Mithralaya';
export const DEFAULT_TITLE = 'Mithralaya — Sarees, Kurtis & Kids Wear Online | Chennai';
export const DEFAULT_DESCRIPTION =
  'Shop sarees, kurtis, churidar sets, tops, nightwear and kidswear at Mithralaya, Chennai. Cash on delivery, easy 7-day returns and delivery across India.';

export const siteUrl = () => (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/+$/, '');
export const absUrl = (path = '/') => (/^https?:\/\//i.test(path) ? path : `${siteUrl()}${path.startsWith('/') ? '' : '/'}${path}`);

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content === undefined || content === null || content === '') {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

// When the backend rendered this page it already wrote fuller structured
// data (shipping, returns, reviews). Keep it for that page; replace it
// once the visitor moves to another page.
const trimPath = (p) => String(p || '').replace(/\/+$/, '') || '/';
const ssrPath = () => document.head.querySelector('meta[name="seo-ssr-path"]')?.getAttribute('content');

const setJsonLd = (data) => {
  document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((s) => s.remove());
  if (ssrPath() && trimPath(ssrPath()) === trimPath(window.location.pathname)) return;
  document.head.querySelectorAll('script[data-seo="ssr"], meta[name="seo-ssr-path"]').forEach((s) => s.remove());
  (Array.isArray(data) ? data : data ? [data] : []).forEach((obj) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo', 'jsonld');
    s.text = JSON.stringify(obj);
    document.head.appendChild(s);
  });
};

export const applySeo = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path,
  image = '/logo512.png',
  robots = 'index, follow',
  type = 'website',
  jsonLd = null
} = {}) => {
  const canonical = absUrl(path || window.location.pathname);
  document.title = title;
  setMeta('name', 'description', description);
  setMeta('name', 'robots', robots);
  setCanonical(canonical);
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:type', type);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', canonical);
  setMeta('property', 'og:image', absUrl(image));
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', absUrl(image));
  setJsonLd(jsonLd);
};

// Hook: re-applies whenever the given values change
export const useSeo = (seo, deps = []) => {
  useEffect(() => {
    if (seo) applySeo(seo);

  }, deps);
};

export const clip = (s, n) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n - 1).replace(/\s+\S*$/, '')}…` : t;
};

export const productPath = (p) =>
  p?.slug ? `/product/${p.slug}` : `/product/${p?._id}${p?.catalogType === 'textile' || p?.subCategory ? '?textile=true' : ''}`;
